import { useEffect, useRef, useState } from 'react'
import gql from 'graphql-tag'
import { apolloClient } from '@/state/apollo-client'

const POLL_INTERVAL_ACTIVE = 4_000
const POLL_INTERVAL_IDLE = 60_000
const IDLE_THRESHOLD = 15 * 60 * 1_000

const LLM_RESPONSE_HTML_POLL_QUERY = gql`
	query llmResponseHtmlPoll($formId: ID!, $recordId: ID!) {
		activeLlmResponseHtml(formId: $formId, recordId: $recordId) {
			id
			html
		}
	}
`

export const useLlmResponseHtmlPoll = (formId: string, recordId: string) => {
	const lastActivityRef = useRef(Date.now())
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const [html, setHtml] = useState<string | null>(null)

	useEffect(() => {
		const updateActivity = () => {
			lastActivityRef.current = Date.now()
		}
		window.addEventListener('mousemove', updateActivity)
		window.addEventListener('keydown', updateActivity)
		window.addEventListener('click', updateActivity)
		return () => {
			window.removeEventListener('mousemove', updateActivity)
			window.removeEventListener('keydown', updateActivity)
			window.removeEventListener('click', updateActivity)
		}
	}, [])

	useEffect(() => {
		const poll = async () => {
			try {
				const result = await apolloClient.query({
					query: LLM_RESPONSE_HTML_POLL_QUERY,
					variables: { formId, recordId },
					fetchPolicy: 'network-only',
				})
				const fetched = result.data?.activeLlmResponseHtml?.html ?? null
				setHtml(fetched)
			} catch {
				// silently ignore transient polling errors
			}

			const isIdle = Date.now() - lastActivityRef.current > IDLE_THRESHOLD
			timeoutRef.current = setTimeout(poll, isIdle ? POLL_INTERVAL_IDLE : POLL_INTERVAL_ACTIVE)
		}
		poll()

		return () => {
			if (timeoutRef.current !== null) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [formId, recordId])

	return html
}
